import React, {useCallback, useEffect, useState} from "react";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../core/services/api/api";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Card, Divider, Tooltip, Typography} from "@mui/material";
import ConfigurableInfoBlock, {ConfigurableInfoBlockInputTypes} from "../../../../../../../components/app-specific/configurable-info-block";
import {InfoOutlined} from "@mui/icons-material";
import {usePartnerInformationController} from "../../../../../../../hooks/use-partner-information";


export const SalesConfigurationsSection = () => {
    const {getPartnerInformation, setCachedPartnerInformation} = usePartnerInformationController();
    const [loading, setLoading] = useState(false);
    const [configs, setConfigs] = useState({});
    const [submitting, setSubmitting] = useState([]);
    const isMounted = useIsMounted();

    /**
     * Fetches the configurations data from the server
     * @type {(function(): Promise<void>)}
     */
    const getConfigurations = useCallback(async () => {
        setLoading(true);
        const partnerInformation = await getPartnerInformation();
        if (!isMounted())
            return;
        setLoading(false);
        if (!!partnerInformation) {
            setConfigs({
                saleRegulationDescription: partnerInformation?.saleRegulationDescription ?? "",
                salePromotionalText: partnerInformation?.salePromotionalText ?? "",
            });
        }
    }, [getPartnerInformation, isMounted]);

    /**
     * As soon as the component is mounted:
     * - Fetches the configurations data from the server
     */
    useEffect(() => {
        void getConfigurations();
    }, [getConfigurations])

    /**
     * Updates the sale regulation description of the partner information.
     * @type {(function(*): Promise<void>)}
     */
    const updateSaleRegulationDescription = useCallback(async (_value) => {
        const value = _value?.trim() ?? "";
        if (value.length === 0) {
            return {resultFlag: false, message: 'The sale regulation description cannot be empty.'};
        } else if (value.length > 1020) {
            return {resultFlag: false, message: 'The sale regulation description cannot be more than 1020 characters.'};
        }
        if (value === configs.saleRegulationDescription) {
            return {resultFlag: true};
        }
        setSubmitting(prevState => [...prevState, 'saleRegulationDescription']);
        const response = await Api.updateSalesRegulation({
            saleRegulation: value,
            updatedProperties: ['saleRegulation']
        })
        if (!isMounted())
            return;
        setSubmitting(prevState => prevState.filter(e => e !== 'saleRegulationDescription'));
        if (response?.resultFlag) {
            setCachedPartnerInformation(prevState => !prevState ? null : ({
                ...prevState,
                saleRegulationDescription: value,
            }));
            setConfigs(prevState => ({
                ...prevState,
                saleRegulationDescription: value,
            }));
        }
        return response;
    }, [configs.saleRegulationDescription, isMounted, setCachedPartnerInformation])


    /**
     * Updates the sale promotional text of the partner information.
     * @type {(function(*): Promise<void>)}
     */
    const updateSalePromotionalText = useCallback(async (_value) => {
        const value = _value?.trim() ?? "";
        if (value.length > 80) {
            return {resultFlag: false, message: 'promotional text cannot be more than 80 characters.'};
        }
        if (value === configs.salePromotionalText) {
            return {resultFlag: true};
        }
        setSubmitting(prevState => [...prevState, 'salePromotionalText']);
        const response = await Api.updateSalesRegulation({
            salePromotionalText: value,
            updatedProperties: ['SalePromotionalText']
        })
        if (!isMounted())
            return;
        setSubmitting(prevState => prevState.filter(e => e !== 'salePromotionalText'));
        if (response?.resultFlag) {
            setCachedPartnerInformation(prevState => !prevState ? null : ({
                ...prevState,
                salePromotionalText: value,
            }));
            setConfigs(prevState => ({
                ...prevState,
                salePromotionalText: value,
            }));
        }
        return response;
    }, [configs.salePromotionalText, isMounted, setCachedPartnerInformation])

    return (
        <>
            {
                loading &&
                Array.from({length: 1}).map((_, index) => (
                    <Grid2 xs={12} key={index}>
                        <div className={'loading access-level w-100'}>
                            <div>
                                <div/>
                            </div>
                        </div>
                    </Grid2>
                ))
            }
            {
                !loading &&
                <>
                    <Grid2 xs={12}>
                        <Card variant={'elevation'} sx={{boxShadow: 'none', backgroundImage: 'none', padding: 2, width: '100%'}}>
                            <Grid2 container spacing={2}>
                                <Grid2 xs={12}>
                                    <Typography variant={'subtitle1'} fontWeight={600}>
                                        Printing Configurations
                                    </Typography>
                                </Grid2>
                                <Grid2 xs={12}>
                                    <Divider variant={'fullWidth'} orientation={'horizontal'}/>
                                </Grid2>
                                <Grid2 xs={12} paddingInline={3}>
                                    <ConfigurableInfoBlock
                                        title={"Sales Regulation Description"}
                                        inputType={ConfigurableInfoBlockInputTypes.input}
                                        inputProps={{
                                            value: configs.saleRegulationDescription ?? "",
                                            type: "text",
                                            placeholder: 'e.g. text',
                                            multiline: true,
                                            minRows: 10,
                                            maxRows: 20,
                                            helperText: `${configs.saleRegulationDescription?.trim()?.length ?? 0}/1020 characters.`,
                                        }}
                                        onValueChanged={(value) => updateSaleRegulationDescription(value)}
                                        loading={submitting.includes('saleRegulationDescription')}
                                        showCustomIcons
                                        customIcons={[
                                            <Tooltip key={1} title={'The description of the sale regulation that is used as the fine print of invoices and orders when printing them.'}>
                                                <InfoOutlined color={'primary'}/>
                                            </Tooltip>
                                        ]}
                                    />
                                </Grid2>
                                <Grid2 xs={12} paddingInline={3}>
                                    <ConfigurableInfoBlock
                                        title={"Sales Promotional Text"}
                                        inputType={ConfigurableInfoBlockInputTypes.input}
                                        inputProps={{
                                            value: configs.salePromotionalText ?? "",
                                            type: "text",
                                            placeholder: 'e.g. text',
                                            helperText: `${configs.salePromotionalText?.trim()?.length ?? 0}/80 characters.`,
                                        }}
                                        onValueChanged={(value) => updateSalePromotionalText(value)}
                                        loading={submitting.includes('salePromotionalText')}
                                        showCustomIcons
                                        customIcons={[
                                            <Tooltip key={1} title={'The promotional text that is used as the fine print of invoices when printing them.'}>
                                                <InfoOutlined color={'primary'}/>
                                            </Tooltip>
                                        ]}
                                    />
                                </Grid2>
                            </Grid2>
                        </Card>
                    </Grid2>
                </>
            }
        </>
    )
}